import React from "react";
import { useQuery } from "@tanstack/react-query";
import { UsersRound } from "lucide-react";
import { Button } from "../components/Button";
import { totalWeightLabel } from "../lib/format";

export default function SharedPage() {
  const statsQuery = useQuery({
    queryKey: ["carpintel_stats"],
    queryFn: () =>
      fetch("/app-api/stats").then((response) => {
        if (!response.ok) throw new Error("Community evidence unavailable");
        return response.json();
      }),
  });

  if (statsQuery.isLoading)
    return <PageMessage text="Building community insights…" />;
  if (statsQuery.error)
    return (
      <PageMessage
        text="Could not load community evidence."
        action="Try again"
        onClick={() => statsQuery.refetch()}
      />
    );

  const stats = statsQuery.data ?? {};
  const personal = stats.my ?? stats;
  const community = stats.community ?? {};
  const ideas = [
    {
      title: "Bait to consider",
      result: community.bestBait,
      personalResult: personal.bestBait,
    },
    {
      title: "Rig to consider",
      result: community.bestRig,
      personalResult: personal.bestRig,
    },
    {
      title: "Time pattern to consider",
      result: community.bestTime,
      personalResult: personal.bestTime,
    },
    {
      title: "Weather pattern to consider",
      result: community.bestWeather,
      personalResult: personal.bestWeather,
    },
    {
      title: "Moon phase to consider",
      result: community.bestMoon,
      personalResult: personal.bestMoon,
    },
  ].filter(
    ({ result, personalResult }) =>
      result &&
      !sameInsightLabel(result?.label, personalResult?.label) &&
      String(result?.label ?? "").trim().toLowerCase() !== "not recorded",
  );

  return (
    <div className="mx-auto max-w-[1000px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <p className="text-sm font-medium text-secondary">
          Anonymous patterns, not a social feed
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-primary">
          Community Insights
        </h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-secondary">
          Community evidence can suggest ideas worth considering. It never
          outranks stronger personal evidence, and this view deliberately avoids
          publishing another angler&apos;s exact peg or turning catches into a
          competitive leaderboard.
        </p>
      </header>

      <section className="rounded-xl border border-accent/30 bg-accent/5 p-4">
        <div className="flex items-start gap-3">
          <UsersRound className="mt-0.5 shrink-0 text-accent" />
          <div>
            <h2 className="font-semibold text-primary">How to read this</h2>
            <p className="mt-1 text-sm leading-6 text-secondary">
              A repeated pattern across many anonymous records is more useful than
              one catch. Treat weak or small datasets as an idea, not an
              instruction. The Advisor remains the place for contextual,
              lake-specific recommendations.
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-primary">
            Community snapshot
          </h2>
          <p className="mt-1 text-sm leading-6 text-secondary">
            Descriptive anonymous community statistics. These are not
            recommendations and never change your personal bests.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <StatCard
            label="Largest community carp"
            value={
              Number(community.biggestCarp?.biggest_fish_lbs || 0) > 0
                ? totalWeightLabel(
                    Number(community.biggestCarp.biggest_fish_lbs),
                  )
                : "—"
            }
            detail={
              community.biggestCarp?.biggest_lake_name
                ? `Caught at ${community.biggestCarp.biggest_lake_name}`
                : "No community carp recorded yet"
            }
          />
          <StatCard
            label="Largest community catfish"
            value={
              Number(community.biggestCatfish?.biggest_fish_lbs || 0) > 0
                ? totalWeightLabel(
                    Number(community.biggestCatfish.biggest_fish_lbs),
                  )
                : "—"
            }
            detail={
              community.biggestCatfish?.biggest_lake_name
                ? `Caught at ${community.biggestCatfish.biggest_lake_name}`
                : "No community catfish recorded yet"
            }
          />
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-primary">
            Patterns worth considering
          </h2>
          <p className="mt-1 text-sm leading-6 text-secondary">
            These are broader anonymous community signals that differ from your
            own current best patterns. They are ideas to explore, not
            lake-specific instructions, and community evidence never overrides
            stronger evidence from your own fishing.
          </p>
        </div>

        {ideas.length ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {ideas.map((idea) => (
              <CommunityIdea
                key={idea.title}
                title={idea.title}
                result={idea.result}
              />
            ))}
          </div>
        ) : (
          <EmptyState text="There is not enough genuinely different community evidence yet. As anonymous data grows, bait, rig, timing, weather and moon-phase patterns may appear here." />
        )}
      </section>
    </div>
  );
}

function sameInsightLabel(a: unknown, b: unknown) {
  const left = String(a ?? "").trim().toLowerCase();
  const right = String(b ?? "").trim().toLowerCase();
  return Boolean(left && right && left === right);
}

function StatCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string | number;
  detail: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-raised p-4">
      <p className="text-xs text-secondary">{label}</p>
      <p className="mt-2 truncate text-2xl font-semibold text-primary">
        {value}
      </p>
      <p className="mt-1 line-clamp-2 text-xs text-secondary">{detail}</p>
    </div>
  );
}

function CommunityIdea({ title, result }: { title: string; result: any }) {
  return (
    <div className="rounded-lg border border-border bg-raised p-4">
      <p className="text-sm text-secondary">{title}</p>
      <div className="mt-2 flex min-w-0 items-end justify-between gap-3">
        <p className="min-w-0 truncate text-base font-semibold text-primary">
          {result.label}
        </p>
        <span className="shrink-0 rounded-full bg-inset px-2 py-0.5 text-xs font-medium text-secondary">
          {result.catch_count}{" "}
          {Number(result.catch_count) === 1
            ? "community catch"
            : "community catches"}
        </span>
      </div>
      <p className="mt-2 text-xs leading-5 text-secondary">
        Broader anonymous community signal only. Check the Advisor for
        lake-specific advice.
      </p>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-secondary">
      {text}
    </div>
  );
}

function PageMessage({
  text,
  action,
  onClick,
}: {
  text: string;
  action?: string;
  onClick?: () => void;
}) {
  return (
    <div className="mx-auto max-w-[760px] px-4 py-8 text-center text-sm text-secondary">
      <p>{text}</p>
      {action && onClick ? (
        <Button variant="tertiary" className="mt-3" onClick={onClick}>
          {action}
        </Button>
      ) : null}
    </div>
  );
}
